import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';

import { useUserStore } from 'src/store';
import { fonts } from 'src/theme/typography';
import { updateProfileColour } from 'src/api/auth';
import { neutralDark, neutralLight } from 'src/theme/palette';

import { Button } from 'src/components/button';

export const PROFILE_COLOUR_OPTIONS = [
  { value: '#BCCF00', label: 'Lime' },
  { value: '#408627', label: 'Green' },
  { value: '#8E5598', label: 'Purple' },
  { value: '#F5817E', label: 'Coral' },
  { value: '#CA5009', label: 'Burnt orange' },
  { value: '#228479', label: 'Teal' },
  { value: '#DA306C', label: 'Pink' },
  { value: '#0068FF', label: 'Blue' },
  { value: '#467BA5', label: 'Steel blue' },
  { value: '#00B9EE', label: 'Cyan' },
];

export const DEFAULT_PROFILE_COLOUR = '#0068FF';

interface ProfileColourModalProps {
  open: boolean;
  onClose: () => void;
}

export function ProfileColourModal({ open, onClose }: ProfileColourModalProps) {
  if (!open) return null;

  return <ProfileColourModalContent open={open} onClose={onClose} />;
}

function ProfileColourModalContent({ open, onClose }: ProfileColourModalProps) {
  const titleId = useId();
  const colourButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { user, setUser } = useUserStore();
  const queryClient = useQueryClient();
  const persistedColour = user?.profileColour ?? DEFAULT_PROFILE_COLOUR;
  const [selected, setSelected] = useState(persistedColour);
  const [saveError, setSaveError] = useState<string | null>(null);
  const selectedColour = PROFILE_COLOUR_OPTIONS.some((colour) => colour.value === selected)
    ? selected
    : DEFAULT_PROFILE_COLOUR;

  const { mutate, isPending } = useMutation({
    mutationFn: () => updateProfileColour(selectedColour),
    onError: () => {
      setSaveError("We couldn't save your profile colour. Please try again.");
    },
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      queryClient.setQueryData(['user', 'my-info'], updatedUser);
      setSaveError(null);
      onClose();
    },
  });

  const selectColour = (colour: string) => {
    setSelected(colour);
    setSaveError(null);
  };

  const focusColourAt = (index: number) => {
    const colour = PROFILE_COLOUR_OPTIONS[index];
    if (!colour) return;

    selectColour(colour.value);
    colourButtonRefs.current[index]?.focus();
  };

  const handleColourKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const lastIndex = PROFILE_COLOUR_OPTIONS.length - 1;
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = index === lastIndex ? 0 : index + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = index === 0 ? lastIndex : index - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    focusColourAt(nextIndex);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      slotProps={{
        paper: {
          sx: {
            width: 327,
            maxWidth: 'calc(100% - 48px)',
            borderRadius: 4,
            mx: 3,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 2 }}>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{
            position: 'absolute',
            top: 24,
            right: 24,
            color: neutralDark.dark,
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Stack spacing={4}>
          <Stack spacing={2} sx={{ pt: 3 }}>
            <Typography
              id={titleId}
              variant="h4"
              component="h2"
              sx={{
                letterSpacing: 0.1,
                fontFamily: fonts.body,
                fontSize: 18,
              }}
            >
              Select your profile colour
            </Typography>
            <Typography variant="body2" sx={{ color: neutralDark.dark }}>
              This is how you&apos;ll appear to others on the platform.
            </Typography>
          </Stack>

          <Box
            role="radiogroup"
            aria-labelledby={titleId}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 44px)',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {PROFILE_COLOUR_OPTIONS.map((colour, index) => (
              <Box
                key={colour.value}
                component="button"
                type="button"
                ref={(node: HTMLButtonElement | null) => {
                  colourButtonRefs.current[index] = node;
                }}
                role="radio"
                onClick={() => selectColour(colour.value)}
                onKeyDown={(event) => handleColourKeyDown(event, index)}
                aria-label={colour.label}
                aria-checked={selectedColour === colour.value}
                tabIndex={selectedColour === colour.value ? 0 : -1}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '6px',
                  bgcolor: colour.value,
                  border: '2px solid',
                  borderColor:
                    selectedColour === colour.value ? neutralDark.darkest : neutralLight.lightest,
                  boxShadow: '0px 1px 3px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  p: 0,
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>

          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Button variant="primary" fullWidth onClick={() => mutate()} disabled={isPending}>
            Save
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
